import { useSelector, useDispatch } from "react-redux";
import { useRef, useState, useEffect, useCallback } from "react";
import { RootState } from "app/store";
import { actions } from "app/configSlice";
import {
  AppPlatform,
  DialogType,
  DeviceNotification,
  LocalStorageKey,
} from "app/types";
import { useScale } from "common/utils";
import { captureScreen } from "common/utils/captureScreen";

export const useNativeMessage = () => {
  const { width, height } = useScale();
  const dispatch = useDispatch();
  const [keyboardHeight, _setKeyboardHeight] = useState(0);
  const keyboardHeightRef = useRef(keyboardHeight);
  const setKeyboardHeight = (value: number) => {
    _setKeyboardHeight(value);
    keyboardHeightRef.current = value;
  };
  const parent = useSelector((state: RootState) => state.user.parent);
  // handleMessage はマウント時に一度だけリスナー登録されるため、
  // 最新の parent を ref 経由で参照する（stale closure 対策）
  const parentRef = useRef(parent);
  useEffect(() => {
    parentRef.current = parent;
  }, [parent]);
  const shakeEnabled = useSelector(
    (state: RootState) => state.config.parentConfig.shake_bug_report,
  );
  const shakeRef = useRef({ enabled: shakeEnabled, uid: parent?.uid });
  useEffect(() => {
    shakeRef.current = { enabled: shakeEnabled, uid: parent?.uid };
  }, [shakeEnabled, parent?.uid]);

  // 端末を振ったときに、いまの画面を撮ってからバグ報告の確認を出す。
  // 確認ダイアログが出たあとに撮ると報告したい画面が隠れるため、先に撮る
  const handleShake = useCallback(async () => {
    const { enabled, uid } = shakeRef.current;
    if (!enabled || !uid) return;
    const capture = await captureScreen();
    dispatch(
      actions.updateDialog({
        type: DialogType.SHAKE_BUG_REPORT,
        value: true,
        args: capture ? { capture } : {},
      }),
    );
  }, [dispatch]);

  const handleUpdateKeyboardPosition = useCallback(
    (enabled: boolean, offset: number) => {
      if (
        window.ReactNativeWebView &&
        (window.parentsAppPlatform === AppPlatform.Android ||
          window.parentsAppPlatform === AppPlatform.Amazon)
      ) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            command: "keyboardAvoidingViewConfig",
            data: {
              enabled: enabled,
              behavior: "position",
              keyboardVerticalOffset: offset,
            },
          }),
        );
      }
    },
    [],
  );

  const handleMessage = (message: MessageEvent) => {
    try {
      const { command, data } = JSON.parse(message.data);
      switch (command) {
        case "notifications": {
          const { status: pushStatus } = data;
          if (
            pushStatus === DeviceNotification.NOT_DETERMINED &&
            parentRef.current
          ) {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                command: "registerNotifications",
                data: { uid: parentRef.current.uid },
              }),
            );
          }
          break;
        }
        case "shake":
          handleShake();
          break;
        case "keyboardDidShow": {
          const { event } = data;
          if (event.height !== keyboardHeightRef.current) {
            localStorage.setItem(LocalStorageKey.KEYBOARD_HEIGHT, event.height);
            setKeyboardHeight(event.height);
          }
          break;
        }
        case "keyboardDidHide":
          handleUpdateKeyboardPosition(false, 0);
          break;
        default:
          break;
      }
    } catch (error) {
      console.warn(JSON.stringify(message));
      console.warn(JSON.stringify(error));
    }
  };

  useEffect(() => {
    const handleInput = (event: Event) => {
      const element = event.target as HTMLElement;
      if (
        element &&
        (element.tagName === "INPUT" || element.tagName === "TEXTAREA")
      ) {
        const rect = element.getBoundingClientRect();
        if (keyboardHeightRef.current > 0) {
          const enabled = height - rect.bottom < keyboardHeightRef.current;
          const offset = enabled ? rect.bottom - height + 10 : 0;
          handleUpdateKeyboardPosition(enabled, offset);
        }
      }
    };
    if (
      window.ReactNativeWebView &&
      (window.parentsAppPlatform === AppPlatform.Android ||
        window.parentsAppPlatform === AppPlatform.Amazon)
    ) {
      window.addEventListener("focus", handleInput, true);
    }
    return () => {
      if (
        window.ReactNativeWebView &&
        (window.parentsAppPlatform === AppPlatform.Android ||
          window.parentsAppPlatform === AppPlatform.Amazon)
      ) {
        window.removeEventListener("focus", handleInput, true);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      (window.parentsAppPlatform === AppPlatform.iOS ||
        window.parentsAppPlatform === AppPlatform.Android) &&
      parent
    ) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          command: "getNotifications",
        }),
      );
    }
  }, [parent]);

  useEffect(() => {
    if ("ReactNativeWebView" in window) {
      if (window.parentsAppPlatform === AppPlatform.iOS) {
        window.addEventListener("message", handleMessage);
      } else {
        document.addEventListener("message", handleMessage as EventListener);
      }

      if (
        window.parentsAppPlatform === AppPlatform.Android ||
        window.parentsAppPlatform === AppPlatform.Amazon
      ) {
        const keyboardHeight = localStorage.getItem(
          LocalStorageKey.KEYBOARD_HEIGHT,
        );
        if (keyboardHeight) {
          setKeyboardHeight(Number(keyboardHeight));
        }
      }
    }

    return () => {
      if ("ReactNativeWebView" in window) {
        if (window.parentsAppPlatform === AppPlatform.iOS) {
          window.removeEventListener("message", handleMessage);
        } else {
          document.removeEventListener(
            "message",
            handleMessage as EventListener,
          );
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleUpdateKeyboardPosition(false, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height]);
};
